
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, ComposedChart } from "recharts";

interface ChartsProps {
  analytics: {
    monthlyData: Array<{
      month: string;
      sales: number;
      orders: number;
      expenses: number;
      profit: number;
    }>;
    expensesByCategory: Array<{
      category: string;
      amount: number;
    }>;
    statusData: Array<{
      name: string;
      value: number;
      color: string;
    }>;
    revenueGrowth: Array<{
      month: string;
      revenue: number;
      expenses: number;
      profit: number;
    }>;
    primaryCurrency: string;
  };
  formatPrice: (amount: number, currency?: string) => string;
}

const ReportsCharts = ({ analytics, formatPrice }: ChartsProps) => {
  return (
    <>
      {analytics.monthlyData.length > 0 && (
        <>
          {/* Monthly Profit Analysis */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly Profit Analysis</CardTitle>
              <CardDescription>Revenue vs Expenses with net profit calculation</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={analytics.monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => {
                      const formattedValue = formatPrice(Number(value), analytics.primaryCurrency);
                      let label = '';
                      
                      if (name === 'sales') {
                        label = 'Revenue';
                      } else if (name === 'expenses') {
                        label = 'Expenses';
                      } else if (name === 'profit') {
                        label = 'Net Profit';
                      }
                      
                      return [formattedValue, label];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="sales" fill="#22c55e" name="Revenue" />
                  <Bar dataKey="expenses" fill="#ef4444" name="Expenses" />
                  <Line type="monotone" dataKey="profit" stroke="#8b5cf6" strokeWidth={3} name="Net Profit" />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {analytics.expensesByCategory.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Expenses by Category</CardTitle>
                  <CardDescription>Where your money is going</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.expensesByCategory}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ category, amount }) => `${category}: ${formatPrice(amount, analytics.primaryCurrency)}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="amount"
                      >
                        {analytics.expensesByCategory.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#8b5cf6', '#ec4899'][index % 7]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatPrice(Number(value), analytics.primaryCurrency)} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {analytics.statusData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Order Status Distribution</CardTitle>
                  <CardDescription>Orders by current status</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {analytics.statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Cumulative Growth Chart */}
          {analytics.revenueGrowth.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Cumulative Business Growth</CardTitle>
                <CardDescription>Track your total revenue, expenses, and profit growth over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={analytics.revenueGrowth}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [
                        formatPrice(Number(value), analytics.primaryCurrency), 
                        name === 'revenue' ? 'Cumulative Revenue' : name === 'expenses' ? 'Cumulative Expenses' : 'Cumulative Profit'
                      ]}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#22c55e" 
                      strokeWidth={3}
                      dot={{ fill: "#22c55e", strokeWidth: 2, r: 6 }}
                      name="Revenue"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="expenses" 
                      stroke="#ef4444" 
                      strokeWidth={3}
                      dot={{ fill: "#ef4444", strokeWidth: 2, r: 6 }}
                      name="Expenses"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="profit" 
                      stroke="#8b5cf6" 
                      strokeWidth={3}
                      dot={{ fill: "#8b5cf6", strokeWidth: 2, r: 6 }}
                      name="Net Profit"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </>
  );
};

export default ReportsCharts;
